import * as React from 'react';
import styled from 'styled-components';
import { ISignInProvider, ISSOProvider } from '../../../../data/types';
import { ErrorText } from '../../../shared/form/ErrorMessage';
import { SSOMethodCard } from './SSOAuth/SSOMethodCard';

export interface ISSOAuthProps {
  signInProviders: ISignInProvider[];
  selectedSSOMethod: string | null;
  onSelectSSOMethod: (method: string | null) => void;
  error?: string;
}

export const SSOAuth = React.memo(
  (props: ISSOAuthProps): React.ReactElement => {
    // Extract SSO providers from sign_in_providers
    const ssoProviders = React.useMemo((): ISSOProvider[] => {
      return props.signInProviders.filter(
        (p: ISignInProvider): p is ISSOProvider =>
          p.type === 'oidc' || p.type === 'saml'
      );
    }, [props.signInProviders]);

    const handleMethodSelect = React.useCallback(
      (provider: ISSOProvider): void => {
        if (props.selectedSSOMethod === provider.id) {
          // Don't deselect - user must choose a method
          return;
        }
        props.onSelectSSOMethod(provider.id);
      },
      [props.selectedSSOMethod, props.onSelectSSOMethod]
    );

    return (
      <React.Fragment>
        <h1 id='sso-method-heading' style={{ margin: '0.25rem 1rem 0.5rem' }}>
          Select SSO Method
        </h1>
        <InputContainer role='form' aria-labelledby='sso-method-heading'>
          {props.error && (
            <ErrorText role='alert' aria-live='polite'>
              {props.error}
            </ErrorText>
          )}
          <MethodList role='radiogroup'>
            {ssoProviders.map(
              (provider: ISSOProvider, index: number): React.ReactElement => (
                <SSOMethodCard
                  key={provider.id}
                  provider={provider}
                  selected={props.selectedSSOMethod === provider.id}
                  onClick={handleMethodSelect}
                  animationDelay={index}
                />
              )
            )}
          </MethodList>
        </InputContainer>
      </React.Fragment>
    );
  },
  (
    prevProps: Readonly<ISSOAuthProps>,
    nextProps: Readonly<ISSOAuthProps>
  ): boolean => {
    return (
      prevProps.signInProviders === nextProps.signInProviders &&
      prevProps.selectedSSOMethod === nextProps.selectedSSOMethod &&
      prevProps.onSelectSSOMethod === nextProps.onSelectSSOMethod &&
      prevProps.error === nextProps.error
    );
  }
);

SSOAuth.displayName = 'SSOAuth';

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0px 16px 8px;
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const MethodList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
